/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListBusinessMemoryContentScopesRequest,
  ListBusinessMemoryContentScopesSecurity,
} from "../models/operations/listbusinessmemorycontentscopes.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildListBusinessMemoryContentScopesQuery,
  ListBusinessMemoryContentScopesQueryData,
  prefetchListBusinessMemoryContentScopes,
  queryKeyListBusinessMemoryContentScopes,
} from "./listBusinessMemoryContentScopes.core.js";
export {
  buildListBusinessMemoryContentScopesQuery,
  type ListBusinessMemoryContentScopesQueryData,
  prefetchListBusinessMemoryContentScopes,
  queryKeyListBusinessMemoryContentScopes,
};

export type ListBusinessMemoryContentScopesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listBusinessMemoryContentScopes businessMemories
 *
 * @remarks
 * List the complete content-scope tree and distinct-memory counts for the active project. Requires organization admin.
 */
export function useListBusinessMemoryContentScopes(
  request?: ListBusinessMemoryContentScopesRequest | undefined,
  security?: ListBusinessMemoryContentScopesSecurity | undefined,
  options?: QueryHookOptions<
    ListBusinessMemoryContentScopesQueryData,
    ListBusinessMemoryContentScopesQueryError
  >,
): UseQueryResult<
  ListBusinessMemoryContentScopesQueryData,
  ListBusinessMemoryContentScopesQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildListBusinessMemoryContentScopesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listBusinessMemoryContentScopes businessMemories
 *
 * @remarks
 * List the complete content-scope tree and distinct-memory counts for the active project. Requires organization admin.
 */
export function useListBusinessMemoryContentScopesSuspense(
  request?: ListBusinessMemoryContentScopesRequest | undefined,
  security?: ListBusinessMemoryContentScopesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ListBusinessMemoryContentScopesQueryData,
    ListBusinessMemoryContentScopesQueryError
  >,
): UseSuspenseQueryResult<
  ListBusinessMemoryContentScopesQueryData,
  ListBusinessMemoryContentScopesQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildListBusinessMemoryContentScopesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setListBusinessMemoryContentScopesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: ListBusinessMemoryContentScopesQueryData,
): ListBusinessMemoryContentScopesQueryData | undefined {
  const key = queryKeyListBusinessMemoryContentScopes(...queryKeyBase);

  return client.setQueryData<ListBusinessMemoryContentScopesQueryData>(
    key,
    data,
  );
}

export function invalidateListBusinessMemoryContentScopes(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "businessMemories",
      "listContentScopes",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllListBusinessMemoryContentScopes(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "businessMemories", "listContentScopes"],
  });
}
